import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyledLabel, StyledLongText } from './styles';
import { useTranslation } from 'react-i18next';
import { useFlashTransition } from 'hooks/useFlashState';
import { applyRef } from 'components/Inputs/props';
import { AIDescription } from './AIDescription';

type DescriptionProps = {
  description: string | null;
  aiDescription?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  setError?: (error: string) => void;
  inputProps?: Record<string, any> & {
    ref?: (instance: HTMLElement | null) => void;
    onBlur?: (e: FocusEvent) => void;
  };
};

export const Description: FC<DescriptionProps> = ({
  label,
  description,
  aiDescription,
  onChange,
  error,
  setError,
  inputProps,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(description || '');
  const [loading, setLoading] = useState(false);
  const [message, showMessage, flashMessage, cancelFlash] =
    useFlashTransition();

  const inputRef = useRef<HTMLElement>(null);

  const { ref, onBlur, disabled, ...restInputProps } = inputProps || {};

  const mergeRef = (el: HTMLElement | null) => {
    applyRef(inputRef, el);
    applyRef(ref, el);
  };

  const onChangeToAIDescription = async (generated: string) => {
    setValue(generated);
    setLoading(false);
    // timeout to ensure input is not disabled before focusing
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleBlur = useCallback(
    (e: FocusEvent) => {
      const trimmedValue = value.trim();
      setValue(trimmedValue);
      onChange?.(trimmedValue);
      onBlur?.(e);
    },
    [onBlur, onChange, value]
  );

  const handleChange = (val: string) => {
    setValue(val);
    setError?.('');
    if (showMessage) {
      cancelFlash();
    }
  };

  useEffect(() => {
    if (error) {
      flashMessage(error);
    } else {
      cancelFlash();
    }
  }, [error, flashMessage, cancelFlash]);

  useEffect(() => {
    setValue(description || '');
  }, [description]);

  const validate = useMemo(
    () => ({
      message,
      showMessage,
    }),
    [message, showMessage]
  );

  return (
    <StyledLongText
      ref={mergeRef}
      label={
        <StyledLabel>
          {label || t('Description')}
          <AIDescription
            aiDescription={!description ? aiDescription || '' : ''}
            onChange={onChangeToAIDescription}
            loading={loading}
            setLoading={setLoading}
          />
        </StyledLabel>
      }
      value={value}
      onChange={handleChange}
      placeholder={t('Enter Description')}
      enableResize={false}
      validate={validate}
      error={error}
      onBlur={handleBlur}
      disabled={disabled || loading}
      {...restInputProps}
    />
  );
};
